const DIAGNOSTIC_COOKIE_NAME = "WEBDEV_49938_COOKIE_TEST";
const REFERRER_COOKIE_SCRIPT_MATCHERS = [
  "referrer-cookie-azure.js",
  "referrer-cookie.js",
];

const emittedErrors = new Set<string>();
let referrerCookieDiagnosticsStarted = false;

type CookieStatus = "missing" | "empty" | "present";
type DiagnosticDetails = Record<string, unknown>;

const isBrowser = () => typeof window !== "undefined";

const isReferrerCookieScriptUrl = (src: string) =>
  REFERRER_COOKIE_SCRIPT_MATCHERS.some((matcher) => src.includes(matcher));

const getSwiCookieStatus = () => {
  const cookies = getCookieMap();
  const cookieNames = ["SWI_Referral", "SWI_RefUrl", "SWI_SiteCatalyst_ID"];

  return Object.fromEntries(
    cookieNames.map((cookieName) => {
      const value = cookies.get(cookieName);

      return [
        cookieName,
        {
          length: value?.length ?? 0,
          status: getCookieStatus(value),
        },
      ];
    }),
  );
};

const canCreateDiagnosticCookie = () => {
  if (!isBrowser()) {
    return { canCreate: false, reason: "not_browser" };
  }

  const value = String(Date.now());
  const encodedName = encodeURIComponent(DIAGNOSTIC_COOKIE_NAME);
  const encodedValue = encodeURIComponent(value);

  document.cookie = `${encodedName}=${encodedValue}; path=/; max-age=60; SameSite=Lax`;
  const canCreate = getCookieMap().get(DIAGNOSTIC_COOKIE_NAME) === value;
  document.cookie = `${encodedName}=; path=/; max-age=0; SameSite=Lax`;

  return {
    canCreate,
    cookieEnabled: window.navigator.cookieEnabled,
    hostname: window.location.hostname,
    protocol: window.location.protocol,
  };
};

const logDiagnosticError = (
  code: string,
  message: string,
  details: DiagnosticDetails = {},
) => {
  if (!isBrowser()) {
    return;
  }

  const errorKey = `${code}:${window.location.href}`;
  if (emittedErrors.has(errorKey)) {
    return;
  }

  emittedErrors.add(errorKey);
  console.error("[Analytics Diagnostics]", code, message, {
    ...getDiagnosticContext(code),
    ...details,
  });
};

const getDiagnosticContext = (checkName: string): DiagnosticDetails => {
  if (!isBrowser()) {
    return { checkName };
  }

  return {
    checkName,
    documentReadyState: document.readyState,
    pageUrl: window.location.href,
    referrer: document.referrer,
    timestamp: new Date().toISOString(),
    timeSincePageStartMs: getTimeSincePageStart(),
  };
};

const getTimeSincePageStart = () => {
  if (!isBrowser()) {
    return undefined;
  }

  return typeof window.performance?.now === "function"
    ? Math.round(window.performance.now())
    : undefined;
};

const getCookieMap = () => {
  const cookies = new Map<string, string>();

  if (!isBrowser()) {
    return cookies;
  }

  for (const cookie of document.cookie.split(";")) {
    const trimmedCookie = cookie.trim();
    if (!trimmedCookie) {
      continue;
    }

    const separatorIndex = trimmedCookie.indexOf("=");
    const rawName =
      separatorIndex === -1
        ? trimmedCookie
        : trimmedCookie.substring(0, separatorIndex);
    const rawValue =
      separatorIndex === -1 ? "" : trimmedCookie.substring(separatorIndex + 1);

    try {
      cookies.set(decodeURIComponent(rawName), decodeURIComponent(rawValue));
    } catch {
      cookies.set(rawName, rawValue);
    }
  }

  return cookies;
};

const getCookieStatus = (value: string | undefined): CookieStatus => {
  if (value === undefined) {
    return "missing";
  }

  if (value.length === 0) {
    return "empty";
  }

  return "present";
};

const handleReferrerCookieScriptError = (event: Event) => {
  const target = event.target;
  if (!(target instanceof HTMLScriptElement)) {
    return;
  }

  const scriptSrc = target.src || target.getAttribute("src") || "";
  if (!isReferrerCookieScriptUrl(scriptSrc)) {
    return;
  }

  logDiagnosticError(
    "ReferrerCookieScriptLoadError",
    "The referrer-cookie script was injected but failed to load.",
    {
      scriptSrc,
      swiCookieStatus: getSwiCookieStatus(),
    },
  );
};

export const runBrowserCookieDiagnostics = () => {
  const result = canCreateDiagnosticCookie();

  if (!result.canCreate) {
    logDiagnosticError(
      "BrowserCookieCreationBlocked",
      "A first-party diagnostic cookie could not be created and read back.",
      {
        cookieTest: result,
        swiCookieStatus: getSwiCookieStatus(),
      },
    );
  }
};

export const startReferrerCookieDiagnostics = () => {
  if (!isBrowser() || referrerCookieDiagnosticsStarted) {
    return;
  }

  referrerCookieDiagnosticsStarted = true;

  window.addEventListener("error", handleReferrerCookieScriptError, true);
};

export const logAdobeScriptLoadError = (src: string, error?: unknown) => {
  logDiagnosticError(
    "AdobeScriptLoadError",
    "The Adobe Launch script failed to load.",
    {
      error,
      scriptSrc: src,
      swiCookieStatus: getSwiCookieStatus(),
    },
  );
};

export const logAdobeSatelliteUnavailable = (src: string) => {
  logDiagnosticError(
    "AdobeSatelliteUnavailable",
    "The Adobe Launch script loaded, but window._satellite.pageBottom was not available.",
    {
      scriptSrc: src,
      swiCookieStatus: getSwiCookieStatus(),
    },
  );
};

export const logAdobePageBottomError = (error: unknown) => {
  logDiagnosticError(
    "AdobePageBottomError",
    "Calling window._satellite.pageBottom failed.",
    {
      error,
      swiCookieStatus: getSwiCookieStatus(),
    },
  );
};
